/* eslint-disable @altermaind/no-magic-numbers */
import { FC, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Button, Card, Svg, Typography, useThemeValue } from '@altermaind/web/ui';
import { ProgressBar } from '@/_shared/components/ProgressBar';
import { RenderLocalesWithComponents } from '@/_shared/components/RenderLocalesWithComponents';
import { Routes } from '@/_shared/constants/routes';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { getDepartmentsCreditsCall } from '../../../state';

export const DivisionByTeam: FC = () => {
  const t = useTranslations('departments.tabs.credits.team');
  const { genericResponsiveValue } = useResponsiveStyle();
  const navigate = useNavigate();
  const [showMore, setShowMore] = useState(false);

  const { teams } = getDepartmentsCreditsCall.hook();
  const { departmentId } = useParams();

  const gapXs = useThemeValue('space', 'xs');

  const goToTeam = (teamId: string) => {
    navigate(
      Routes.DepartmentsTeamsDetail.replace(':teamId', teamId).replace(
        ':departmentId',
        departmentId || '',
      ),
    );
  };

  return (
    <Box mt='s'>
      <Box
        mt='xl'
        display='flex'
        flexDirection='row'
        alignItems='center'
        justifyContent='space-between'
      >
        <Typography variant='h3'>{t('title')}</Typography>
        {teams.length > 3 && (
          <Box onClick={() => setShowMore(!showMore)} style={{ cursor: 'pointer' }}>
            {showMore ? (
              <Box display='flex' flexDirection='row' alignItems='center'>
                <Typography variant='p3/light'>{t('showLess')}</Typography>
                <Box style={{ transform: 'rotate(180deg)' }}>
                  <Svg name='down' size='s' fill='grey/light' />
                </Box>
              </Box>
            ) : (
              <Box display='flex' flexDirection='row' alignItems='center'>
                <Typography variant='p3/light'>{t('showMore')}</Typography>
                <Svg name='down' size='s' fill='grey/light' />
              </Box>
            )}
          </Box>
        )}
      </Box>
      <Typography mt='xs' variant='p3/light'>
        {t('description')}
      </Typography>
      <Box
        mt='s'
        style={{ gap: gapXs }}
        display='grid'
        gridTemplateColumns={genericResponsiveValue({
          breakPoints: {
            desktop: 'repeat(3, 1fr)',
            tablet: 'repeat(1, 1fr)',
            mobile: 'repeat(1, 1fr)',
          },
          defaultValue: 'repeat(1, 1fr)',
        })}
      >
        {(showMore ? teams : teams.slice(0, 3)).map(team => (
          <Card key={team.teamName}>
            <Box
              alignItems='center'
              display='flex'
              flexDirection='row'
              justifyContent='space-between'
              mt='xxxs'
              pb='xs'
            >
              <Typography variant='h3'>
                {team.teamName !== 'unknown' ? team.teamName : t('noTeamName')}
              </Typography>
              <Box alignItems='center' flexDirection='row' display='flex'>
                <Svg name='users' size='s' />
                <Typography ml='xxs' variant='h1'>
                  {team.userNumber}
                </Typography>
              </Box>
            </Box>
            <Box>
              <ProgressBar
                currentPercentage={Number(
                  ((team.averageConsumption / team.assigned) * 100).toFixed(0),
                )}
              />
              <Box display='flex' justifyContent='space-between' mt='xxs'>
                <Box flex='2'>
                  <Typography variant='p2/light'>
                    <RenderLocalesWithComponents
                      text={t('creditPerPerson', {
                        total: '!{TOTAL}!',
                      })}
                      placeholders={{
                        '!{TOTAL}!': (
                          <Typography as='span' variant='p2/light' fontWeight='500'>
                            {team.averageConsumption}
                          </Typography>
                        ),
                      }}
                    />
                  </Typography>
                </Box>
                <Box textAlign='right' flex='1'>
                  <Typography variant='p2/light' fontWeight='500'>
                    {((team.averageConsumption / team.assigned) * 100).toFixed(0)}%
                  </Typography>
                </Box>
              </Box>
            </Box>
            {team.teamName !== 'unknown' && (
              <Button onClick={() => goToTeam(team.teamId)} variant='positive' mt='m'>
                {t('buttonCta')}
              </Button>
            )}
          </Card>
        ))}
      </Box>
    </Box>
  );
};
